import type { Vec2 } from "./types";

export const WORLD_SIZE = 1000;
export const CENTER: Vec2 = { x: WORLD_SIZE / 2, y: WORLD_SIZE / 2 };

export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function subtract(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(vector: Vec2, amount: number): Vec2 {
  return { x: vector.x * amount, y: vector.y * amount };
}

export function length(vector: Vec2): number {
  return Math.hypot(vector.x, vector.y);
}

export function normalize(vector: Vec2): Vec2 {
  const magnitude = length(vector);
  if (magnitude < 0.00001) {
    return { x: 0, y: 0 };
  }
  return scale(vector, 1 / magnitude);
}

export function direction(angle: number): Vec2 {
  return { x: Math.cos(angle), y: Math.sin(angle) };
}

export function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function clampMagnitude(vector: Vec2, maximum: number): Vec2 {
  const magnitude = length(vector);
  return magnitude > maximum ? scale(vector, maximum / magnitude) : vector;
}

export function shortestAngle(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

export function moveAngle(from: number, to: number, maximum: number): number {
  const difference = shortestAngle(from, to);
  return from + clamp(difference, -maximum, maximum);
}

export function distanceSquared(a: Vec2, b: Vec2): number {
  const x = a.x - b.x;
  const y = a.y - b.y;
  return x * x + y * y;
}

export function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}
